import { SelectChangeEvent, Stack, TextField, Typography } from '@mui/material';

import { TemplateHeaderFormat, WhatsAppHeaderComponent } from 'domains/content/types';
import Select from 'domains/core/components/Select';

import MergeTagPicker from '../MergeTagPicker';

const HEADER_FORMAT_OPTIONS: { value: TemplateHeaderFormat | 'NONE'; label: string }[] = [
    { value: 'NONE', label: 'None' },
    { value: TemplateHeaderFormat.TEXT, label: 'Text' },
    { value: TemplateHeaderFormat.IMAGE, label: 'Image' },
    { value: TemplateHeaderFormat.VIDEO, label: 'Video' },
    { value: TemplateHeaderFormat.DOCUMENT, label: 'Document' },
    { value: TemplateHeaderFormat.LOCATION, label: 'Location' },
];

const MEDIA_PLACEHOLDERS: Partial<Record<TemplateHeaderFormat, string>> = {
    [TemplateHeaderFormat.IMAGE]: 'https://example.com/image.png',
    [TemplateHeaderFormat.VIDEO]: 'https://example.com/video.mp4',
    [TemplateHeaderFormat.DOCUMENT]: 'https://example.com/document.pdf',
};

const MEDIA_HINTS: Partial<Record<TemplateHeaderFormat, string>> = {
    [TemplateHeaderFormat.IMAGE]: 'JPEG or PNG · max 5 MB',
    [TemplateHeaderFormat.VIDEO]: 'MP4 · max 16 MB',
    [TemplateHeaderFormat.DOCUMENT]: 'PDF, DOC, XLS, PPT · max 100 MB',
};

export type Props = {
    header: WhatsAppHeaderComponent | null;
    onChange: (header: WhatsAppHeaderComponent | null) => void;
};

const WhatsAppTemplateHeader = ({ header, onChange }: Props) => {
    const selectedFormat = header?.format ?? 'NONE';

    const handleFormatChange = (e: SelectChangeEvent<unknown>) => {
        const value = e.target.value;
        if (value === 'NONE') {
            onChange(null);
        } else {
            onChange({ type: 'HEADER', format: value as TemplateHeaderFormat });
        }
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!header) return;
        onChange({ ...header, text: e.target.value });
    };

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!header) return;
        onChange({ ...header, url: e.target.value });
    };

    const handleLocationChange = (field: 'latitude' | 'longitude' | 'name' | 'address') => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!header) return;
        onChange({
            ...header,
            location: { ...(header.location ?? { latitude: '', longitude: '' }), [field]: e.target.value },
        });
    };

    const insertMergeTagIntoText = (tag: string) => {
        if (!header) return;
        const current = header.text ?? '';
        onChange({ ...header, text: current + tag });
    };

    return (
        <Stack spacing={2} data-testid="whatsapp-template-header">
            <Typography variant="subtitle2" fontWeight={600}>
                Header
            </Typography>

            <Select
                data-testid="header-format-select"
                label="Type"
                value={selectedFormat}
                onChange={handleFormatChange}
                size="small"
                fullWidth={false}
                sx={{ minWidth: 180 }}
                values={HEADER_FORMAT_OPTIONS}
                inputProps={{ 'aria-label': 'Header format' }}
            />

            {header?.format === TemplateHeaderFormat.TEXT && (
                <Stack spacing={0.5}>
                    <TextField
                        data-testid="header-text-input"
                        label="Header text"
                        value={header.text ?? ''}
                        onChange={handleTextChange}
                        inputProps={{ maxLength: 60, 'aria-label': 'Header text' }}
                        helperText={`${(header.text ?? '').length} / 60`}
                        size="small"
                        fullWidth
                    />
                    <MergeTagPicker handleSelect={insertMergeTagIntoText} />
                    <Typography variant="caption" color="text.secondary">
                        Supports 1 merge tag
                    </Typography>
                </Stack>
            )}

            {header?.format &&
                [TemplateHeaderFormat.IMAGE, TemplateHeaderFormat.VIDEO, TemplateHeaderFormat.DOCUMENT].includes(
                    header.format
                ) && (
                    <TextField
                        data-testid="header-url-input"
                        label="Media URL"
                        value={header.url ?? ''}
                        onChange={handleUrlChange}
                        placeholder={MEDIA_PLACEHOLDERS[header.format]}
                        helperText={MEDIA_HINTS[header.format]}
                        size="small"
                        fullWidth
                        inputProps={{ 'aria-label': 'Header media URL' }}
                    />
                )}

            {header?.format === TemplateHeaderFormat.LOCATION && (
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            data-testid="header-latitude-input"
                            label="Latitude"
                            value={header.location?.latitude ?? ''}
                            onChange={handleLocationChange('latitude')}
                            size="small"
                            sx={{ flex: 1 }}
                            inputProps={{ 'aria-label': 'Latitude' }}
                        />
                        <TextField
                            data-testid="header-longitude-input"
                            label="Longitude"
                            value={header.location?.longitude ?? ''}
                            onChange={handleLocationChange('longitude')}
                            size="small"
                            sx={{ flex: 1 }}
                            inputProps={{ 'aria-label': 'Longitude' }}
                        />
                    </Stack>
                    <TextField
                        data-testid="header-location-name-input"
                        label="Location name (optional)"
                        value={header.location?.name ?? ''}
                        onChange={handleLocationChange('name')}
                        size="small"
                        fullWidth
                        inputProps={{ 'aria-label': 'Location name' }}
                    />
                    <TextField
                        data-testid="header-location-address-input"
                        label="Address (optional)"
                        value={header.location?.address ?? ''}
                        onChange={handleLocationChange('address')}
                        size="small"
                        fullWidth
                        inputProps={{ 'aria-label': 'Location address' }}
                    />
                </Stack>
            )}
        </Stack>
    );
};

export default WhatsAppTemplateHeader;
